import { useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { navItems } from "../constants/data";
import { filterNavByPermission } from "../utils/permissions";
import {
  MdDashboard, MdPeople, MdFolder, MdMeetingRoom,
  MdCampaign, MdBarChart, MdSettings,
  MdAssignment, MdHistory, MdNotifications,
  MdChevronRight,
} from "react-icons/md";

const ICONS = {
  dashboard:    <MdDashboard    size={20} />,
  staff:        <MdPeople       size={20} />,
  contract:     <MdAssignment   size={20} />,
  documents:    <MdFolder       size={20} />,
  activities:   <MdMeetingRoom  size={20} />,
  announcements:<MdCampaign     size={20} />,
  notifications:<MdNotifications size={20} />,
  reports:      <MdBarChart     size={20} />,
  "activity-log":<MdHistory     size={20} />,
  settings:     <MdSettings     size={20} />,
};

const SIDEBAR = "#3D1A08";
const APP_VERSION = "v1.0.8.0";
const GOLD    = "var(--gold)";
const WHITE   = "#F5E6D8";
const KH      = "'Khmer OS Siemreap','Noto Sans Khmer',sans-serif";
const MO      = "'Moul','Noto Sans Khmer',serif";

const groups  = ["ទូទៅ", "គ្រប់គ្រង", "ផ្សេងទៀត"];

export default function Sidebar({ activeNav, setActiveNav }) {
  const { user } = useAuth();
  const { t } = useLanguage();
  const visibleNav = useMemo(
    () => filterNavByPermission(navItems, user),
    [user]
  );
  const [expanded, setExpanded] = useState(() => {
    const openMap = {};
    navItems.forEach(item => {
      if (item.children?.some(c => c.key === activeNav)) {
        openMap[item.key] = true;
      }
    });
    return openMap;
  });

  const toggleExpand = (key) => {
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const isActive = (item) => {
    if (item.key === activeNav) return true;
    return item.children?.some(c => c.key === activeNav) || false;
  };

  const handleClick = (item) => {
    if (item.children?.length) {
      toggleExpand(item.key);
      if (!expanded[item.key]) {
        setActiveNav(item.children[0].key);
      }
    } else {
      setActiveNav(item.key);
    }
  };

  return (
    <div style={{
      width: 210,
      background: SIDEBAR,
      display: "flex", flexDirection: "column",
      flexShrink: 0, overflow: "hidden",
    }}>
      <div style={{
        padding: "20px 16px 16px",
        borderBottom: "1px solid rgba(255,255,255,0.08)",
        textAlign: "center",
      }}>
        <div
          style={{
          width: 100,
          height: 100,
          borderRadius: "50%",
          background: "rgba(196,149,106,0.15)",
          border: `2px solid ${GOLD}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 10px",
          overflow: "hidden",
        }}
>
        <img
          src={require("../assets/logo.png")}
          alt="Logo"
          style={{
            width: "70%",
            height: "70%",
            objectFit: "contain",
          }}
        />
      </div>
        <p style={{ color: WHITE, fontSize: 13, fontWeight: 700,
                    lineHeight: 1.4, fontFamily: MO }}>
          {t("អគ្គាធិការដ្ឋាន")}
        </p>
        <p style={{ color: GOLD, fontSize: 10, marginTop: 3, fontFamily: KH }}>
          {t("ប្រព័ន្ធគ្រប់គ្រង")}
        </p>
      </div>

      <nav style={{ flex: 1, padding: "10px 8px", overflowY: "auto" }}>
        {groups.map(group => {
          const items = visibleNav.filter(n => n.group === group);
          if (items.length === 0) return null;
          return (
            <div key={group} style={{ marginBottom: 6 }}>
              <p style={{
                fontSize: 10,
                color: "rgba(196,149,106,0.55)",
                padding: "8px 10px 3px",
                fontFamily: KH, letterSpacing: 0.3,
              }}>{t(group)}</p>

              {items.map(item => {
                const active   = isActive(item);
                const hasChild = item.children?.length > 0;
                const open     = expanded[item.key];

                return (
                  <div key={item.key}>
                    <button
                      onClick={() => handleClick(item)}
                      style={{
                        width: "100%",
                        display: "flex", alignItems: "center", gap: 9,
                        padding: "9px 10px", borderRadius: 8,
                        border: "none", cursor: "pointer",
                        background: active ? "rgba(196,149,106,0.2)" : "transparent",
                        color: active ? GOLD : "rgba(245,230,216,0.65)",
                        fontSize: 13, textAlign: "left", marginBottom: 1,
                        transition: "all 0.15s",
                        borderLeft: active
                          ? `3px solid ${GOLD}`
                          : "3px solid transparent",
                        fontFamily: KH,
                        justifyContent: "space-between",
                      }}
                      onMouseEnter={e => {
                        if (!active) e.currentTarget.style.background = "rgba(196,149,106,0.08)";
                      }}
                      onMouseLeave={e => {
                        if (!active) e.currentTarget.style.background = "transparent";
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                        <span style={{ fontSize:20, flexShrink:0, display:'flex', alignItems:'center' }}>{ICONS[item.key]}</span>
                        <span>{t(item.label)}</span>
                      </div>
                      {hasChild && (
                        <span style={{
                          color: active ? GOLD : "rgba(245,230,216,0.4)",
                          transition: "transform 0.2s",
                          transform: open ? "rotate(90deg)" : "rotate(0deg)",
                          display: "inline-flex",
                          alignItems: "center",
                        }}><MdChevronRight size={16}/></span>
                      )}
                    </button>

                    {hasChild && open && (
                      <div style={{
                        marginLeft: 18,
                        marginBottom: 4,
                        borderLeft: "1px solid rgba(196,149,106,0.25)",
                        paddingLeft: 8,
                      }}>
                        {item.children.map(child => {
                          const childActive = child.key === activeNav;
                          return (
                            <button
                              key={child.key}
                              onClick={() => setActiveNav(child.key)}
                              style={{
                                width: "100%",
                                display: "flex", alignItems: "center", gap: 8,
                                padding: "7px 10px", borderRadius: 7,
                                border: "none", cursor: "pointer",
                                background: childActive
                                  ? "rgba(196,149,106,0.18)"
                                  : "transparent",
                                color: childActive
                                  ? GOLD
                                  : "rgba(245,230,216,0.55)",
                                fontSize: 12, textAlign: "left",
                                marginBottom: 1, fontFamily: KH,
                                transition: "all 0.15s",
                              }}
                              onMouseEnter={e => {
                                if (!childActive) e.currentTarget.style.background = "rgba(196,149,106,0.08)";
                              }}
                              onMouseLeave={e => {
                                if (!childActive) e.currentTarget.style.background = "transparent";
                              }}
                            >
                              <span style={{
                                width: 6, height: 6, borderRadius: "50%",
                                background: childActive ? GOLD : "rgba(196,149,106,0.4)",
                                flexShrink: 0,
                              }} />
                              {t(child.label)}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </nav>

      <div style={{
        padding: "6px 14px",
        display: "flex", justifyContent: "center", alignItems: "center", gap: 6,
      }}>
        <span style={{
          fontSize: 10, color: "rgba(196,149,106,0.5)",
          fontFamily: "monospace", letterSpacing: 0.5,
        }}>
          {APP_VERSION}
        </span>
        <span style={{ fontSize:10, color:"rgba(196,149,106,0.25)" }}>·</span>
        <span style={{
          fontSize: 10, color: "rgba(196,149,106,0.35)",
          fontFamily: "monospace",
        }}>
        </span>
      </div>
    </div>
  );
}